import { __awaiter } from 'tslib';
import React, { useState, useMemo, useRef, useCallback, useEffect } from 'react';
import { getNotificationsUnreadCount, postNotificationEvents, getNotificationsFeed, getNotificationsPopup, sendNotificationEventsOnPageExit } from '../../services/notificationsService.js';
import { NotificationDialog } from './components/notification-dialog/NotificationDialog.js';
import { NotificationDrawer } from './components/notification-drawer/NotificationDrawer.js';
import { POPUP_COOLDOWN_HOURS } from './constants.js';
import { isShowableItem } from './dialogs/catalog.js';
import { opensAnotherSite } from './links.js';
import { WhatsNewBoundary } from './WhatsNewBoundary.js';
import { WhatsNewContext } from './WhatsNewContext.js';

const renderDefaultPopup = (props) => React.createElement(NotificationDialog, Object.assign({}, props));
const renderDefaultDrawer = (props) => React.createElement(NotificationDrawer, Object.assign({}, props));
/** Calls a render prop while rendering inside the boundary, so a throw there is caught too. */
const RenderSlot = ({ render }) => React.createElement(React.Fragment, null, render());
const EVENT_TYPES = ["shown", "click", "dismiss"];
const emptyQueue = () => ({ shown: new Set(), click: new Set(), dismiss: new Set() });
const INITIAL_DRAWER = {
    open: false,
    anchorEl: null,
    items: [],
    hasMore: false,
    isLoading: false,
    failedSkip: null,
};
const POPUP_COOLDOWN_MS = POPUP_COOLDOWN_HOURS * 60 * 60 * 1000;
/** When the customer last closed the page's pop-up in this scope: shared by every tab, kept over reloads. */
const popupClosedAtKey = (scope) => `peek.whatsNew.popupClosedAt:${scope}`;
/** Set once What's New had something in this scope (organization); the sidebar row stays from then on. */
const hasAnnouncementsKey = (scope) => `peek.whatsNew.hasAnnouncements:${scope}`;
const readRememberedAnnouncements = (scope) => {
    try {
        return window.localStorage.getItem(hasAnnouncementsKey(scope)) === "true";
    }
    catch (_a) {
        return false;
    }
};
const rememberAnnouncements = (scope) => {
    try {
        window.localStorage.setItem(hasAnnouncementsKey(scope), "true");
    }
    catch (_a) {
        // Storage blocked: the row follows the feed alone.
    }
};
// Storage can be unavailable (private mode, blocked site data). Then there's no cooldown, and the
// receipts on the back end still keep a slide that was reached from popping up again.
const readPopupClosedAt = (scope) => {
    try {
        const closedAt = Number(window.localStorage.getItem(popupClosedAtKey(scope)));
        return Number.isFinite(closedAt) && closedAt > 0 ? closedAt : null;
    }
    catch (_a) {
        return null;
    }
};
const markPopupClosed = (scope) => {
    try {
        window.localStorage.setItem(popupClosedAtKey(scope), String(Date.now()));
    }
    catch (_a) {
        // See readPopupClosedAt.
    }
};
/** Within the cooldown since the pop-up last closed. A mark from the future (a clock set back) doesn't hold. */
const isPopupCoolingDown = (scope) => {
    const closedAt = readPopupClosedAt(scope);
    if (closedAt === null)
        return false;
    const elapsed = Date.now() - closedAt;
    return elapsed >= 0 && elapsed < POPUP_COOLDOWN_MS;
};
/**
 * The one place deciding which slides the page may pop up: one this build can draw with words to
 * show, not read yet, and not expired. The API already leaves expired messages out; `expiresAt` is
 * checked in case an item ever carries it.
 */
const canPopUpSlide = (slide, now) => isShowableItem(slide) && !slide.isRead && !(slide.expiresAt && new Date(slide.expiresAt).getTime() <= now);
/** Pages are fetched by offset, so one can repeat an entry the list already has (a re-pinned one). */
const appendPage = (items, page) => {
    const seen = new Set(items.map((item) => item.id));
    return [...items, ...page.filter((item) => !seen.has(item.id))];
};
const WhatsNewProvider = ({ placement, onNavigate, sessionScope = "default", renderPopup = renderDefaultPopup, renderDrawer = renderDefaultDrawer, onError, onEvent, children, }) => {
    const [unreadCount, setUnreadCount] = useState(0);
    const [feedHasItems, setFeedHasItems] = useState(false);
    // Whether anything was ever published, as the API says (unread-count's `hasAnnouncements`), or,
    // until it does, whether What's New ever had something in this organization, in this browser.
    const [everPublished, setEverPublished] = useState(undefined);
    const rememberedAnnouncements = useMemo(() => readRememberedAnnouncements(sessionScope), [sessionScope]);
    // What the row knows is one organization's. Another one starts over, in this very render, so nothing
    // reads the last organization's count or word as this one's (nor remembers the row for it).
    const [rowScope, setRowScope] = useState(sessionScope);
    if (rowScope !== sessionScope) {
        setRowScope(sessionScope);
        setUnreadCount(0);
        setFeedHasItems(false);
        setEverPublished(undefined);
    }
    const [popup, setPopup] = useState({ open: false, slides: [], session: 0 });
    const [drawer, setDrawer] = useState(INITIAL_DRAWER);
    // How many times the drawer has opened; one that failed to render gets another try with the next.
    const [drawerSession, setDrawerSession] = useState(0);
    const queueRef = useRef(emptyQueue());
    // Only the latest feed request lands: a page from another organization, or one overtaken by a
    // fresh first page, is dropped.
    const feedRequestRef = useRef(0);
    const popupOriginRef = useRef(null);
    // What the customer clicked or dismissed on this visit, per organization. Those events go out when
    // the pop-up closes, so a page the main button opens can ask for its pop-up before they land, and be
    // offered the same announcement again: it is left out here instead.
    const actedOnRef = useRef(new Set());
    const actedOnKey = useCallback((id) => `${sessionScope}\n${id}`, [sessionScope]);
    const markActedOn = useCallback((id) => actedOnRef.current.add(actedOnKey(id)), [actedOnKey]);
    const isMountedRef = useRef(true);
    const scopeRef = useRef(sessionScope);
    scopeRef.current = sessionScope;
    // The latest callbacks and page without re-running the effects that close over them.
    const onNavigateRef = useRef(onNavigate);
    onNavigateRef.current = onNavigate;
    const onErrorRef = useRef(onError);
    onErrorRef.current = onError;
    const onEventRef = useRef(onEvent);
    onEventRef.current = onEvent;
    const placementRef = useRef(placement);
    placementRef.current = placement;
    // Analytics never stand in the way of what the customer did: a product handler that throws is
    // reported, and the pop-up still closes, the link still opens.
    const tell = useCallback((event) => {
        var _a, _b;
        try {
            (_a = onEventRef.current) === null || _a === void 0 ? void 0 : _a.call(onEventRef, event);
        }
        catch (error) {
            (_b = onErrorRef.current) === null || _b === void 0 ? void 0 : _b.call(onErrorRef, error instanceof Error ? error : new Error(String(error)));
        }
    }, []);
    // The pop-up on screen, for its analytics: its slides in order, the ones already told as seen, and
    // whether it opened on its own on the page or from a list entry. Refs only, so the helpers are stable.
    const popupSlidesRef = useRef([]);
    const seenSlidesRef = useRef(new Set());
    const popupSurface = useCallback(() => (popupOriginRef.current === "drawer" ? "drawer" : "pop_up"), []);
    const slideIndexOf = useCallback((id) => popupSlidesRef.current.findIndex((slide) => slide.id === id) + 1, []);
    const openPopup = useCallback((slides, origin, demo = false) => {
        popupOriginRef.current = origin;
        popupSlidesRef.current = slides;
        seenSlidesRef.current = new Set();
        setPopup((previous) => ({ open: true, slides, session: previous.session + 1, demo }));
        tell({
            type: "popup_opened",
            placement: placementRef.current,
            surface: popupSurface(),
            slideCount: slides.length,
            announcementIds: slides.map((slide) => slide.id),
        });
    }, [tell, popupSurface]);
    /**
     * Before the pop-up closes: which slide was on screen and how the customer closed it. Once: a second
     * close on the way out finds it closed already.
     */
    const tellPopupClosed = useCallback((slide, method) => {
        if (!popupOriginRef.current)
            return;
        tell({
            type: "popup_closed",
            placement: placementRef.current,
            surface: popupSurface(),
            method,
            slideIndex: slideIndexOf(slide.id),
            slideCount: popupSlidesRef.current.length,
        });
    }, [tell, popupSurface, slideIndexOf]);
    useEffect(() => () => {
        isMountedRef.current = false;
    }, []);
    // What's New never blocks the page it sits on: a failed call leaves the last known state.
    const refreshUnreadCount = useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        const scope = scopeRef.current;
        try {
            const { count, hasAnnouncements } = yield getNotificationsUnreadCount();
            // An answer for an organization the customer has since left is not this one's.
            if (!isMountedRef.current || scope !== scopeRef.current)
                return;
            setUnreadCount(count);
            if (hasAnnouncements !== undefined)
                setEverPublished(hasAnnouncements);
        }
        catch (_a) {
            // Keep the last count.
        }
    }), []);
    /**
     * Sends everything queued since the last flush, one call per event type, then refreshes the badge.
     * A type whose call fails goes back in the queue for the next flush; the others still go (receipts
     * are upserts, so sending one twice is harmless).
     */
    const flush = useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        const queue = queueRef.current;
        queueRef.current = emptyQueue();
        const types = EVENT_TYPES.filter((type) => queue[type].size > 0);
        if (!types.length)
            return;
        for (const type of types) {
            try {
                yield postNotificationEvents({ type, ids: Array.from(queue[type]) });
            }
            catch (_a) {
                queue[type].forEach((id) => queueRef.current[type].add(id));
            }
        }
        yield refreshUnreadCount();
    }), [refreshUnreadCount]);
    const closePopup = useCallback(() => {
        popupOriginRef.current = null;
        setPopup((previous) => (Object.assign(Object.assign({}, previous), { open: false, demo: false })));
        void flush();
    }, [flush]);
    /**
     * The customer closed the page's pop-up (X, Esc, Close, or a main button that stays in the app): no
     * page pops up for a while.
     */
    const closePagePopup = useCallback(() => {
        if (popupOriginRef.current === "page")
            markPopupClosed(sessionScope);
        closePopup();
    }, [closePopup, sessionScope]);
    useEffect(() => {
        void refreshUnreadCount();
    }, [refreshUnreadCount, sessionScope]);
    // One entry is enough to know the feed isn't empty; a failed probe leaves the row hidden until the
    // unread count or the drawer says otherwise.
    useEffect(() => {
        let isCurrent = true;
        getNotificationsFeed({ skip: 0, limit: 1 })
            .then((page) => {
            if (isCurrent && isMountedRef.current)
                setFeedHasItems(page.items.length > 0);
        })
            .catch(() => undefined);
        return () => {
            isCurrent = false;
        };
    }, [sessionScope]);
    // Each pop-up page asks for its slides when it opens, unless the customer closed a pop-up less than
    // POPUP_COOLDOWN_HOURS ago, in any tab. Leaving a page closes whatever it popped up. With nothing
    // to render the pop-up, don't ask at all: asking would spend slides unseen.
    const canPopUp = renderPopup !== null;
    useEffect(() => {
        if (popupOriginRef.current)
            closePopup();
        if (!placement || !canPopUp || isPopupCoolingDown(sessionScope))
            return;
        let isCurrent = true;
        getNotificationsPopup(placement)
            .then((items) => {
            // One the customer opened from the drawer meanwhile stays, and one closed in another tab
            // meanwhile counts here too; the page asks again next time.
            if (!isCurrent || popupOriginRef.current || isPopupCoolingDown(sessionScope))
                return;
            const slides = items.filter((slide) => canPopUpSlide(slide, Date.now()) && !actedOnRef.current.has(actedOnKey(slide.id)));
            if (!slides.length)
                return;
            openPopup(slides, "page");
        })
            .catch(() => undefined);
        return () => {
            isCurrent = false;
        };
    }, [placement, sessionScope, closePopup, canPopUp, actedOnKey, openPopup]);
    // Closed in another tab: this tab's pop-up from the page closes too (its own cooldown has begun).
    useEffect(() => {
        const key = popupClosedAtKey(sessionScope);
        const handleStorage = (event) => {
            if (event.key === key && event.newValue && popupOriginRef.current === "page")
                closePopup();
        };
        window.addEventListener("storage", handleStorage);
        return () => window.removeEventListener("storage", handleStorage);
    }, [sessionScope, closePopup]);
    // Leaving the site mid pop-up still reports the slides that were reached: a keepalive request,
    // since the page won't be around for an ordinary one to finish.
    useEffect(() => {
        const handlePageHide = () => {
            const queue = queueRef.current;
            queueRef.current = emptyQueue();
            EVENT_TYPES.forEach((type) => sendNotificationEventsOnPageExit({ type, ids: Array.from(queue[type]) }));
        };
        window.addEventListener("pagehide", handlePageHide);
        return () => {
            window.removeEventListener("pagehide", handlePageHide);
            void flush();
        };
    }, [flush]);
    // -- Pop-up ------------------------------------------------------------------------------------
    const handleSlideShown = useCallback((id) => {
        queueRef.current.shown.add(id);
        if (!popupOriginRef.current || seenSlidesRef.current.has(id))
            return;
        seenSlidesRef.current.add(id);
        tell({
            type: "slide_seen",
            placement: placementRef.current,
            surface: popupSurface(),
            announcementId: id,
            slideIndex: slideIndexOf(id),
        });
    }, [tell, popupSurface, slideIndexOf]);
    // A click or a dismissal goes even for a slide not on screen long enough to count as shown: the
    // back end records it as shown with them.
    const handlePrimary = useCallback((slide) => {
        var _a;
        queueRef.current.click.add(slide.id);
        markActedOn(slide.id);
        // Once per click: a second one on the closing pop-up finds it closed already.
        if (slide.cta && popupOriginRef.current) {
            tell({
                type: "main_button_clicked",
                placement: placementRef.current,
                surface: popupSurface(),
                announcementId: slide.id,
                slideIndex: slideIndexOf(slide.id),
                variant: slide.cta.variant,
            });
        }
        // The dialog swaps the card for the demo player in place; the pop-up stays open.
        if (((_a = slide.cta) === null || _a === void 0 ? void 0 : _a.variant) === "watch_demo") {
            tell({ type: "demo_started", placement: placementRef.current, announcementId: slide.id, surface: "pop_up" });
            return;
        }
        // Another site opens in a new tab: the pop-up stays open here, on the same slide, for when the
        // customer comes back. Its click goes out with the rest when the pop-up closes.
        if (slide.cta && opensAnotherSite(slide.cta.href)) {
            onNavigateRef.current(slide.cta.href);
            return;
        }
        tellPopupClosed(slide, "main_button");
        closePagePopup();
        if (slide.cta)
            onNavigateRef.current(slide.cta.href);
    }, [closePagePopup, tell, markActedOn, popupSurface, slideIndexOf, tellPopupClosed]);
    // "Close", the X or Esc. On the page's pop-up it dismisses the slide on screen. One opened from the
    // drawer was already clicked (read), so it just closes.
    const handleDismiss = useCallback((slide, method = "x") => {
        tellPopupClosed(slide, method);
        // A slide whose main button was clicked (another site, a demo) is read: closing it dismisses
        // nothing, which would only contradict the click.
        if (popupOriginRef.current === "page" && !queueRef.current.click.has(slide.id)) {
            queueRef.current.dismiss.add(slide.id);
        }
        markActedOn(slide.id);
        closePagePopup();
    }, [closePagePopup, markActedOn, tellPopupClosed]);
    const handleDemoFinished = useCallback((slide) => tell({ type: "demo_completed", placement: placementRef.current, announcementId: slide.id }), [tell]);
    // -- Drawer ------------------------------------------------------------------------------------
    const loadFeed = useCallback((skip) => __awaiter(void 0, void 0, void 0, function* () {
        const request = ++feedRequestRef.current;
        setDrawer((previous) => (Object.assign(Object.assign({}, previous), { isLoading: true, failedSkip: null })));
        try {
            const page = yield getNotificationsFeed({ skip });
            if (!isMountedRef.current || request !== feedRequestRef.current)
                return;
            if (page.items.length)
                setFeedHasItems(true);
            setDrawer((previous) => (Object.assign(Object.assign({}, previous), { isLoading: false, items: skip ? appendPage(previous.items, page.items) : page.items, hasMore: page.hasMore })));
        }
        catch (_a) {
            if (!isMountedRef.current || request !== feedRequestRef.current)
                return;
            // A first page that fails leaves nothing from before (another organization, an older visit).
            setDrawer((previous) => skip
                ? Object.assign(Object.assign({}, previous), { isLoading: false, failedSkip: skip }) : Object.assign(Object.assign({}, previous), { isLoading: false, failedSkip: skip, items: [], hasMore: false }));
        }
    }), []);
    // The feed is aimed by the active organization: a page still on its way from another one doesn't
    // carry over.
    const feedScopeRef = useRef(sessionScope);
    useEffect(() => {
        if (feedScopeRef.current === sessionScope)
            return;
        feedScopeRef.current = sessionScope;
        feedRequestRef.current += 1;
        setDrawer(INITIAL_DRAWER);
    }, [sessionScope]);
    // Opening the drawer sends no events (appendix §5.5).
    // With nothing to show the list with, the entry never counts as open.
    const canShowDrawer = renderDrawer !== null;
    const openDrawer = useCallback((anchorEl) => {
        if (!canShowDrawer)
            return;
        // Handed straight to an onClick, it gets the click event: that anchors nothing.
        const anchor = anchorEl instanceof Element ? anchorEl : null;
        setDrawer((previous) => (Object.assign(Object.assign({}, previous), { open: true, anchorEl: anchor })));
        setDrawerSession((session) => session + 1);
        void loadFeed(0);
        tell({ type: "drawer_opened", placement: placementRef.current });
    }, [loadFeed, canShowDrawer, tell]);
    const closeDrawer = useCallback(() => setDrawer((previous) => (Object.assign(Object.assign({}, previous), { open: false }))), []);
    const handleLoadMore = useCallback(() => {
        if (drawer.isLoading || !drawer.hasMore)
            return;
        void loadFeed(drawer.items.length);
        tell({ type: "drawer_load_more", placement: placementRef.current, loaded: drawer.items.length });
    }, [drawer.isLoading, drawer.hasMore, drawer.items.length, loadFeed, tell]);
    const handleRetry = useCallback(() => {
        if (drawer.failedSkip !== null)
            void loadFeed(drawer.failedSkip);
    }, [drawer.failedSkip, loadFeed]);
    /**
     * A drawer entry's pop-up, alone, on its card or on its demo player. Opening it is the entry's click
     * (it reads as read), sent when the pop-up closes with the rest of what happened in it; the customer
     * chose to look, so no cooldown starts.
     */
    const openFromDrawer = useCallback((item, demo) => {
        queueRef.current.click.add(item.id);
        markActedOn(item.id);
        openPopup([item], "drawer", demo);
    }, [markActedOn, openPopup]);
    // The entry's link. A demo plays in the pop-up's own player, as from a pop-up; with no pop-up to
    // play it in, the link opens instead, as every other link does.
    const handleEntryClick = useCallback((item) => {
        var _a;
        closeDrawer();
        tell({ type: "drawer_entry_clicked", placement: placementRef.current, announcementId: item.id, target: "link" });
        const isDemo = ((_a = item.cta) === null || _a === void 0 ? void 0 : _a.variant) === "watch_demo";
        if (isDemo) {
            tell({ type: "demo_started", placement: placementRef.current, announcementId: item.id, surface: "drawer" });
        }
        if (isDemo && canPopUp) {
            openFromDrawer(item, true);
            return;
        }
        markActedOn(item.id);
        if (item.cta)
            onNavigateRef.current(item.cta.href);
        postNotificationEvents({ type: "click", ids: [item.id] })
            .catch(() => undefined)
            .then(refreshUnreadCount);
    }, [closeDrawer, refreshUnreadCount, tell, markActedOn, canPopUp, openFromDrawer]);
    // The entry itself opens its pop-up, on its card.
    const handleEntryOpen = useCallback((item) => {
        closeDrawer();
        tell({
            type: "drawer_entry_clicked",
            placement: placementRef.current,
            announcementId: item.id,
            target: "pop_up",
        });
        openFromDrawer(item, false);
    }, [closeDrawer, tell, openFromDrawer]);
    // The row stays once anything was published, even after it is switched off or expires: only before
    // the very first announcement is there nothing to show. The API's word decides; without it, what this
    // browser has already seen does.
    const hasAnnouncements = feedHasItems || unreadCount > 0 || (everPublished !== null && everPublished !== void 0 ? everPublished : rememberedAnnouncements);
    useEffect(() => {
        if (hasAnnouncements)
            rememberAnnouncements(sessionScope);
    }, [hasAnnouncements, sessionScope]);
    const contextValue = useMemo(() => ({ hasAnnouncements, unreadCount, isDrawerOpen: drawer.open, openDrawer, closeDrawer, refreshUnreadCount }), [hasAnnouncements, unreadCount, drawer.open, openDrawer, closeDrawer, refreshUnreadCount]);
    const handlePopupError = useCallback((error) => {
        var _a;
        if (popupOriginRef.current)
            closePopup();
        (_a = onErrorRef.current) === null || _a === void 0 ? void 0 : _a.call(onErrorRef, error);
    }, [closePopup]);
    // What the drawer held when it failed may be what broke it: the next open starts from the first page.
    const handleDrawerError = useCallback((error) => {
        var _a;
        feedRequestRef.current += 1;
        setDrawer(INITIAL_DRAWER);
        (_a = onErrorRef.current) === null || _a === void 0 ? void 0 : _a.call(onErrorRef, error);
    }, []);
    return (React.createElement(WhatsNewContext.Provider, { value: contextValue },
        children,
        renderPopup && (React.createElement(WhatsNewBoundary, { resetKey: popup.session, onError: handlePopupError },
            React.createElement(RenderSlot, { render: () => renderPopup({
                    open: popup.open,
                    slides: popup.slides,
                    onPrimary: handlePrimary,
                    onDismiss: handleDismiss,
                    onSlideShown: handleSlideShown,
                    onClose: closePopup,
                    onDemoFinished: handleDemoFinished,
                    startWithDemo: popup.demo,
                }) }))),
        renderDrawer && (React.createElement(WhatsNewBoundary, { resetKey: drawerSession, onError: handleDrawerError },
            React.createElement(RenderSlot, { render: () => renderDrawer({
                    open: drawer.open,
                    anchorEl: drawer.anchorEl,
                    items: drawer.items,
                    hasMore: drawer.hasMore,
                    isLoading: drawer.isLoading,
                    hasError: drawer.failedSkip !== null,
                    onClose: closeDrawer,
                    onEntryClick: handleEntryClick,
                    // With nothing to show the pop-up with, only the entry's link does anything.
                    onEntryOpen: canPopUp ? handleEntryOpen : undefined,
                    onLoadMore: handleLoadMore,
                    onRetry: handleRetry,
                }) })))));
};

export { WhatsNewProvider, WhatsNewProvider as default, hasAnnouncementsKey, popupClosedAtKey };
